// Per-context manual order. Instead of one global `rank` field (which can't
// represent independent orderings — see n3oltd/work#771), each context (a
// person's list, a sprint, a client) stores its own ordered issue-id lists on
// the app-data branch. Reordering goes through /api/orders/reorder, which merges
// the changed group into the current file SERVER-side under a lock — so
// concurrent reorders of different groups of a shared context can't clobber.
import { graphql, reorderContextGroup } from './api'
import { CONTEXT_ORDER } from './github-queries'
import { appConfig } from './query'

const BRANCH = 'app-data'

/** Who positioned an issue in a shared context, and when — attribution for the
 *  cross-context "flag". Personal lists (`mine/<login>`) don't need it: the
 *  owner is the context. The server stamps it; the client never sets it. */
export type Placement = { by: string; at: string }

export type ContextOrder = {
  /** Ordered issue node-ids per group key (e.g. a priority band). */
  groups: Record<string, string[]>
  /** Per-issue placement attribution, keyed by node-id. Absent on old files
   *  and on personal contexts — flags then fall back to no/derived credit. */
  placements?: Record<string, Placement>
}

// Frozen so the shared empty fallback can never be mutated by accident.
export const EMPTY_ORDER: ContextOrder = Object.freeze({ groups: {} })

/** `mine/<login>`, `sprint/<name>`, … → orders/<context>.json */
function pathFor(context: string): string {
  return `orders/${context}.json`
}

/** Parse an order blob, returning EMPTY_ORDER when it's missing or malformed so
 *  one bad file never throws a view away. Shared by the per-context read and the
 *  cross-context index. */
export function parseContextOrder(text: string | null | undefined): ContextOrder {
  if (!text) return EMPTY_ORDER
  try {
    const parsed = JSON.parse(text) as ContextOrder
    if (parsed && typeof parsed.groups === 'object' && parsed.groups !== null) return parsed
  } catch {
    /* malformed → treat as empty rather than throw the view away */
  }
  return EMPTY_ORDER
}

export async function loadContextOrder(context: string): Promise<ContextOrder> {
  const data = await graphql<{ repository: { object: { text?: string } | null } | null }>({
    query: CONTEXT_ORDER,
    variables: { owner: appConfig.owner, name: appConfig.repo, expr: `${BRANCH}:${pathFor(context)}` },
  })
  return parseContextOrder(data.repository?.object?.text)
}

/** Persist a group's new order. The server reads the current file and merges
 *  just this group, so it's safe even for shared, multi-writer contexts. */
export async function reorderGroup(
  context: string,
  group: string,
  orderedIds: string[],
): Promise<void> {
  await reorderContextGroup({ context, group, orderedIds })
}

/**
 * Place `orderedIds` into `group` and remove those issues from every other
 * group (an issue belongs to exactly one group). Returns a new ContextOrder.
 */
export function placeInGroup(order: ContextOrder, group: string, orderedIds: string[]): ContextOrder {
  const moved = new Set(orderedIds)
  const groups: Record<string, string[]> = {}
  for (const [key, ids] of Object.entries(order.groups)) {
    if (key === group) continue
    groups[key] = ids.filter((id) => !moved.has(id))
  }
  groups[group] = orderedIds
  if (!order.placements) return { groups }
  // Attribution is the server's to stamp, but prune it to ids still placed
  // somewhere so the optimistic cache doesn't carry credit for issues that just
  // left the order. NB: the authoritative version is `mergeOrder` in
  // server/github/order-merge.ts — keep the prune rule in sync (the server also
  // *adds* credit for ids entering a group, which the client can't know).
  const live = new Set(Object.values(groups).flat())
  const placements: Record<string, Placement> = {}
  for (const [id, p] of Object.entries(order.placements)) {
    if (live.has(id)) placements[id] = p
  }
  return Object.keys(placements).length ? { groups, placements } : { groups }
}

/**
 * Restore just `group` to its pre-reorder order (`prev`) within the latest cache
 * (`base`) — the rollback for a failed reorder. Touching only the one group
 * means a concurrent reorder of another group of a shared context isn't
 * clobbered by restoring a whole-context snapshot. Pure.
 */
export function rollbackGroup(base: ContextOrder, prev: ContextOrder, group: string): ContextOrder {
  const groups = { ...base.groups }
  const prevIds = prev.groups[group] ?? []
  if (prevIds.length) groups[group] = prevIds
  else delete groups[group]
  return { ...base, groups }
}

/**
 * Comparator for items in a group: those listed in `ids` sort by their
 * position; everything else (new / never-placed) sorts after, oldest first,
 * so the unplaced tail stays stable as issues are added.
 */
export function byManualOrder(
  ids: string[],
): (a: { id: string; createdAt: string }, b: { id: string; createdAt: string }) => number {
  const pos = new Map(ids.map((id, i) => [id, i]))
  return (a, b) => {
    const pa = pos.get(a.id) ?? Infinity
    const pb = pos.get(b.id) ?? Infinity
    return pa - pb || a.createdAt.localeCompare(b.createdAt)
  }
}
